import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { CalendarIcon, UsersIcon, TargetIcon, EyeIcon, ArrowRightIcon } from '../components/icons/Icons';
import { useSettings } from '../context/SettingsContext';
import './About.css';

const About = () => {
  const { settings } = useSettings();

  return (
    <>
      <Helmet>
        <title>About Us | Fuwali Investments Zambia Limited</title>
        <meta name="description" content="Learn about Fuwali Investments Zambia Limited - our story, vision, and commitment to driving national development through innovation and sustainable impact." />
        <link rel="canonical" href="https://fuwalizambia.com/about" />
        <meta property="og:title" content="About Us | Fuwali Investments Zambia Limited" />
        <meta property="og:description" content="Learn about Fuwali Investments Zambia Limited - our story, vision, and commitment to driving national development." />
        <meta property="og:url" content="https://fuwalizambia.com/about" />
        <meta property="og:type" content="website" />
      </Helmet>

      {/* Page Hero */}
      <section className="page-hero" style={settings.hero_bg_about ? { backgroundImage: `url(${settings.hero_bg_about})` } : {}}>
        <div className="page-hero-overlay"></div>
        <div className="container">
          <span className="page-hero-label">About Us</span>
          <h1 className="page-hero-title">The Identity</h1>
          <p className="page-hero-subtitle">
            Building a diversified investment company that drives national development through innovation, job creation, and sustainable impact.
          </p>
        </div>
      </section>

      {/* Our Story Section */}
      <section className="section story-section">
        <div className="container">
          <div className="story-grid">
            <div className="story-content">
              <span className="section-label">Our Story</span>
              <h2 className="section-title text-left">A Bold Vision Realized</h2>
              <p className="story-text">
                Fuwali Investments Zambia Limited was established with a bold vision: to build a diversified investment company that drives national development through innovation, job creation, and sustainable impact.
              </p>
              <p className="story-text">
                Born out of a desire to empower communities and transform industries, Fuwali was created not only to invest, but to uplift. From its inception, the company has been committed to contributing meaningfully to Zambia's economic growth while creating opportunities that improve lives.
              </p>
              <div className="story-founders">
                <div className="founder-card">
                  <div className="founder-avatar">
                    <UsersIcon size={32} />
                  </div>
                  <div className="founder-info">
                    <h4>Chipego Mali</h4>
                    <span>Co-Founder</span>
                  </div>
                </div>
                <div className="founder-card">
                  <div className="founder-avatar">
                    <UsersIcon size={32} />
                  </div>
                  <div className="founder-info">
                    <h4>Chipego Chifuwe</h4>
                    <span>Co-Founder</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="story-timeline">
              <div className="timeline-item">
                <div className="timeline-icon">
                  <CalendarIcon size={24} />
                </div>
                <div className="timeline-content">
                  <span className="timeline-date">October 17, 2024</span>
                  <h4>Company Founded</h4>
                  <p>Fuwali Investments Zambia Limited officially established</p>
                </div>
              </div>
              <div className="timeline-item">
                <div className="timeline-icon">
                  <TargetIcon size={24} />
                </div>
                <div className="timeline-content">
                  <span className="timeline-date">Vision Set</span>
                  <h4>Strategic Focus</h4>
                  <p>Core sectors identified: Supply, Logistics, Finance, Agriculture</p>
                </div>
              </div>
              <div className="timeline-item">
                <div className="timeline-icon">
                  <EyeIcon size={24} />
                </div>
                <div className="timeline-content">
                  <span className="timeline-date">Ongoing</span>
                  <h4>Growth & Impact</h4>
                  <p>Expanding partnerships and community initiatives</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Vision & Mission Section */}
      <section className="section vision-section">
        <div className="container">
          <div className="vision-grid">
            <div className="vision-card">
              <div className="vision-icon">
                <EyeIcon size={40} />
              </div>
              <h3>Our Vision</h3>
              <p>
                To be a leading force in fostering innovative, impact-driven investment solutions that uplift communities and contribute to national progress.
              </p>
            </div>
            <div className="vision-card">
              <div className="vision-icon">
                <TargetIcon size={40} />
              </div>
              <h3>Our Mission</h3>
              <p>
                To drive national development through strategic investments in key sectors, creating sustainable value for our stakeholders while empowering communities across Zambia.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="section values-section">
        <div className="container">
          <span className="section-label center">Our Values</span>
          <h2 className="section-title">What Guides Us</h2>
          <p className="section-subtitle">
            Our core values shape every decision we make and every partnership we forge.
          </p>
          <div className="values-grid">
            <div className="value-card">
              <span className="value-number">01</span>
              <h4>Innovation</h4>
              <p>Embracing new ideas and approaches to create lasting solutions</p>
            </div>
            <div className="value-card">
              <span className="value-number">02</span>
              <h4>Integrity</h4>
              <p>Operating with transparency, honesty, and ethical standards</p>
            </div>
            <div className="value-card">
              <span className="value-number">03</span>
              <h4>Impact</h4>
              <p>Measuring success by the positive change we create in communities</p>
            </div>
            <div className="value-card">
              <span className="value-number">04</span>
              <h4>Sustainability</h4>
              <p>Building for the long term while protecting our environment</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-content">
            <h2>Partner With Us</h2>
            <p>Join us in building a sustainable future for Zambia through strategic investments.</p>
            <Link to="/contact" className="btn btn-secondary">
              Get In Touch <ArrowRightIcon size={18} />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default About;
